import { DeckProps } from '@deck.gl/core'
import { IconLayer, LineLayer } from '@deck.gl/layers'
import { ControlPosition, Map, useMap } from '@vis.gl/react-google-maps'
import {
  VehicleRoutes,
  VehicleEvents,
  VehicleEvent,
} from 'interfaces/IClaimsSearching'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { getEventIcon } from 'tools/helper'
import {
  getIconSvgString,
  getRouteIconSvgString,
  getSpeedColor,
  getTooltipData,
} from './claimsMapUtils'
import Supercluster from 'supercluster'
import ClaimsMapsLegend from '../claimsMapLegend'
import ClaimsMapEventSummary from '../claimsMapEventSummary'
import { createClusterSvg, svgToDataURL, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from 'tools/mapHelper'

interface IClaimsMapsProps {
  carrierName: string
  routesList?: VehicleRoutes[]
  safetyEventsList?: VehicleEvents | null
  selectedEventId?: number | null
  selectedEventData?: VehicleEvent | null
  onEventSelect: (
    eventId: number | null,
    openDetails?: boolean,
    showRoute?: boolean
  ) => void
  onViewRoute?: (
    vehicleId: string,
    eventTime: string,
    companyEldAuthenticationId: number
  ) => void
  handleOpenDetails: (open: boolean) => void
}

interface ClusteredPoint {
  position: [number, number]
  id: string
  type: string
  iconName: string
  isCluster: boolean
  pointCount?: number
  clusterId?: number
  time?: string
  vin?: string
}

interface RouteMarker {
  position: [number, number]
  id: string
  type: 'start' | 'end'
  vehicleId: string
}

interface RouteSegment {
  sourcePosition: [number, number]
  targetPosition: [number, number]
  speed: number
  speedLimit: number
  vehicleId: string
}

const DeckGLOverlay = (props: DeckProps) => {
  const map = useMap()
  const overlay = useMemo(
    () =>
      new GoogleMapsOverlay({
        ...props,
        getCursor: ({ isHovering, isDragging }) => {
          if (map) {
            if (isHovering) {
              map.setOptions({ draggableCursor: 'pointer' })
            } else {
              map.setOptions({ draggableCursor: '' })
            }
          }
          return isDragging ? 'grabbing' : 'grab'
        },
        getTooltip: ({ object, layer }) => {
          return getTooltipData(object, layer)
        },
      }),
    [map]
  )

  useEffect(() => {
    if (map) {
      overlay.setMap(map)
      return () => {
        overlay.setMap(null)
        map.setOptions({ draggableCursor: '' })
      }
    }
  }, [map, overlay])

  useEffect(() => {
    overlay.setProps(props)
  }, [overlay, props])

  return null
}

const ClaimsMap: React.FC<IClaimsMapsProps> = (props) => {
  const {
    carrierName,
    routesList,
    safetyEventsList,
    selectedEventId,
    selectedEventData,
    onEventSelect,
    handleOpenDetails,
  } = props

  const map = useMap()
  const [defaultZoom] = useState(DEFAULT_MAP_ZOOM)
  const [defaultCenter] = useState(DEFAULT_MAP_CENTER)
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_MAP_ZOOM)

  useEffect(() => {
    if (!map) return

    const handleZoomChanged = () => {
      const zoom = map.getZoom()
      if (zoom !== undefined) {
        setCurrentZoom(zoom)
      }
    }
    const listener = map.addListener('zoom_changed', handleZoomChanged)
    return () => listener.remove()
  }, [map])

  useEffect(() => {
    if (safetyEventsList && map) {
      map.setCenter(DEFAULT_MAP_CENTER)
      map.setZoom(DEFAULT_MAP_ZOOM)
    }
  }, [safetyEventsList, map])

  useEffect(() => {
    if (selectedEventId && map && safetyEventsList) {
      const selectedEvent = safetyEventsList.safetyEvents?.find(
        (event) => event.id === selectedEventId
      )
      if (selectedEvent) {
        map.panTo({
          lat: selectedEvent.latitude,
          lng: selectedEvent.longitude,
        })
        map.setZoom(14)
      }
    }
  }, [selectedEventId, map, safetyEventsList])

  const routeMarkers = useMemo(() => {
    if (!routesList || routesList.length === 0) return []

    const markers: RouteMarker[] = []

    routesList.forEach((vehicleRoute: VehicleRoutes) => {
      const routes = vehicleRoute.routes
      if (!routes || routes.length < 2) return

      const vehicleId = vehicleRoute.vehicleId.assetEldId || 'unknown'

      const startPoint = routes[0]
      markers.push({
        position: [startPoint.longitude, startPoint.latitude],
        id: `start-${vehicleId}`,
        type: 'start',
        vehicleId: vehicleId,
      })

      const endPoint = routes[routes.length - 1]
      markers.push({
        position: [endPoint.longitude, endPoint.latitude],
        id: `end-${vehicleId}`,
        type: 'end',
        vehicleId: vehicleId,
      })
    })

    return markers
  }, [routesList])

  const routeSegments = useMemo(() => {
    if (!routesList || routesList.length === 0) return []

    const segments: RouteSegment[] = []

    routesList.forEach((vehicleRoute: VehicleRoutes) => {
      const routes = vehicleRoute.routes
      if (!routes || routes.length < 2) return

      for (let i = 0; i < routes.length - 1; i++) {
        const currentPoint = routes[i]
        const nextPoint = routes[i + 1]

        segments.push({
          sourcePosition: [currentPoint.longitude, currentPoint.latitude],
          targetPosition: [nextPoint.longitude, nextPoint.latitude],
          speed: currentPoint.speed,
          speedLimit: currentPoint.speedLimit,
          vehicleId: vehicleRoute.vehicleId.assetEldId || 'unknown',
        })
      }
    })

    return segments
  }, [routesList])

  const geoJsonPoints = useMemo(() => {
    if (!safetyEventsList?.safetyEvents) return []

    return safetyEventsList.safetyEvents.map((event: VehicleEvent) => ({
      type: 'Feature' as const,
      properties: {
        id: event.id,
        eventType: event.type,
        iconName: getEventIcon(event.type),
        eventTime: event.time,
        vin: event.vin,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [event.longitude, event.latitude],
      },
    }))
  }, [safetyEventsList])

  const clusteredData = useMemo(() => {
    if (!geoJsonPoints.length) return []

    const supercluster = new Supercluster({
      radius: 80,
      maxZoom: 12,
      minZoom: 0,
      minPoints: 2,
    })

    supercluster.load(geoJsonPoints)

    const bbox = [-180, -85, 180, 85] as [number, number, number, number]
    const zoom = Math.floor(currentZoom)

    const clusters = supercluster.getClusters(bbox, zoom)

    return clusters.map((cluster): ClusteredPoint => {
      const [longitude, latitude] = cluster.geometry.coordinates

      if (cluster.properties?.cluster) {
        return {
          position: [longitude, latitude],
          id: `cluster-${cluster.properties.cluster_id}`,
          type: 'cluster',
          iconName: 'cluster',
          isCluster: true,
          pointCount: cluster.properties.point_count,
          clusterId: cluster.properties.cluster_id,
        }
      } else {
        const props = cluster.properties
        return {
          position: [longitude, latitude],
          id: props.id,
          type: props.eventType,
          iconName: props.iconName,
          isCluster: false,
          time: props.eventTime,
          vin: props.vin,
        }
      }
    })
  }, [geoJsonPoints, currentZoom])

  const handleClusterClick = useCallback(
    (info: any) => {
      const { object } = info

      if (object.isCluster && map) {
        const supercluster = new Supercluster({
          radius: 80,
          maxZoom: 12,
          minZoom: 0,
          minPoints: 2,
        })

        supercluster.load(geoJsonPoints)

        const expansionZoom = supercluster.getClusterExpansionZoom(
          object.clusterId
        )

        map.setCenter({
          lat: object.position[1],
          lng: object.position[0],
        })
        map.setZoom(Math.min(expansionZoom + 1, 20))
      }
    },
    [map, geoJsonPoints]
  )

  const handleEventClick = useCallback(
    (info: any) => {
      const { object } = info

      if (!object.isCluster && onEventSelect) {
        onEventSelect(Number(object.id), false, true)
      }
    },
    [onEventSelect]
  )

  const layers = useMemo(() => {
    const layerArray = []

    if (routeSegments && routeSegments.length > 0) {
      layerArray.push(
        new LineLayer({
          id: 'route-segments',
          data: routeSegments,
          getSourcePosition: (d) => d.sourcePosition,
          getTargetPosition: (d) => d.targetPosition,
          getColor: (d) => getSpeedColor(d.speed, d.speedLimit),
          getWidth: 6,
          widthMinPixels: 3,
          widthMaxPixels: 12,
          pickable: false,
        })
      )
    }

    if (routeMarkers && routeMarkers.length > 0) {
      layerArray.push(
        new IconLayer({
          id: 'route-markers',
          data: routeMarkers,
          getIcon: (d) => ({
            url: svgToDataURL(getRouteIconSvgString(d.type)),
            width: 50,
            height: 50,
          }),
          sizeScale: 4,
          getSize: 10,
          getPosition: (d) => d.position,
          pickable: false,
        })
      )
    }

    if (clusteredData && clusteredData.length > 0) {
      layerArray.push(
        new IconLayer({
          id: 'safety-events-clustered',
          data: clusteredData,
          getIcon: (d) => {
            if (d.isCluster) {
              const actualSize =
                d.pointCount! < 10 ? 50 : d.pointCount! < 100 ? 65 : 80
              return {
                url: svgToDataURL(createClusterSvg(d.pointCount || 0)),
                width: actualSize,
                height: actualSize,
              }
            } else {
              return {
                url: svgToDataURL(getIconSvgString(d.iconName)),
                width: 100,
                height: 100,
              }
            }
          },
          sizeScale: 4,
          getSize: (d) => (d.isCluster ? 12 : 10),
          getPosition: (d) => d.position,
          pickable: true,
          onClick: (info) => {
            if (info.object?.isCluster) {
              handleClusterClick(info)
            } else {
              handleEventClick(info)
            }
          },
        })
      )
    }

    return layerArray
  }, [
    routeSegments,
    routeMarkers,
    clusteredData,
    handleClusterClick,
    handleEventClick,
  ])

  return (
    <Map
      defaultZoom={defaultZoom}
      defaultCenter={defaultCenter}
      gestureHandling={'greedy'}
      disableDefaultUI={true}
      clickableIcons={false}
      mapId={'SAFETY_EVENTS_MAP'}
    >
      <ClaimsMapsLegend position={ControlPosition.RIGHT_TOP} />

      {selectedEventData && (
        <ClaimsMapEventSummary
          carrierName={carrierName}
          position={ControlPosition.LEFT_BOTTOM}
          eventData={selectedEventData}
          handleOpenDetails={handleOpenDetails}
          onEventSelect={onEventSelect}
        />
      )}
      <DeckGLOverlay layers={layers} />
    </Map>
  )
}

export default ClaimsMap
